import type { ButtonHTMLAttributes, ReactNode } from 'react';
import { Spinner } from './icons';

type Variant = 'primary' | 'secondary' | 'ghost' | 'critical';
type Size = 'md' | 'lg';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
  loading?: boolean;
  /** Stretch to the container width (default on auth screens). */
  block?: boolean;
  leading?: ReactNode;
}

const variants: Record<Variant, string> = {
  primary:
    'bg-brand text-on-brand hover:bg-brand-hover active:bg-brand-active shadow-sm disabled:bg-line disabled:text-ink-3 disabled:shadow-none',
  secondary:
    'bg-surface text-ink border border-line-strong hover:bg-canvas active:bg-line disabled:text-ink-3',
  ghost: 'bg-transparent text-brand hover:bg-brand-soft active:bg-brand-soft disabled:text-ink-3',
  critical:
    'bg-critical text-white hover:opacity-90 disabled:bg-line disabled:text-ink-3 disabled:shadow-none',
};

const sizes: Record<Size, string> = {
  md: 'min-h-12 px-4 text-sm',
  lg: 'min-h-12 px-5 text-base',
};

/** 48px control height, visible focus ring (global), loading spinner keeps the label width. */
export function Button({
  variant = 'primary',
  size = 'md',
  loading = false,
  block = false,
  leading,
  className = '',
  children,
  disabled,
  ...rest
}: ButtonProps) {
  return (
    <button
      {...rest}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      className={[
        'relative inline-flex select-none items-center justify-center gap-2 rounded-md font-medium',
        'transition-[background-color,color,transform,box-shadow] duration-[var(--pa-dur-fast)] ease-[var(--pa-ease)]',
        'active:scale-[0.99] disabled:cursor-not-allowed disabled:active:scale-100',
        variants[variant],
        sizes[size],
        block ? 'w-full' : '',
        className,
      ].join(' ')}
    >
      {loading ? (
        <span className="absolute inset-0 flex items-center justify-center">
          <Spinner size={20} />
        </span>
      ) : null}
      <span className={['inline-flex items-center gap-2', loading ? 'invisible' : ''].join(' ')}>
        {leading}
        {children}
      </span>
    </button>
  );
}
